import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { ArrowLeft, Loader2, Save } from "lucide-react";

const FALLBACK_BRANDS = [
    { code: "ciilock", name: "CiiLOCK" },
    { code: "durabal", name: "Durabal" },
    { code: "siigmatic", name: "SiiGMATIC" },
];

const QueueSetting = () => {
    const [limits, setLimits] = useState({
        domain: "",
        domainCutoffDays: "",
        email: "",
        connect: "",
        expandi_scraping: "",
        leverage_enhancement: "",
    });
    const [brandEmailLimits, setBrandEmailLimits] = useState({});
    const [brands, setBrands] = useState(FALLBACK_BRANDS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const orderedBrands = useMemo(() => {
        const rows = Array.isArray(brands) && brands.length ? brands : FALLBACK_BRANDS;
        return [...rows].sort((a, b) => {
            if (a.code === "ciilock") return -1;
            if (b.code === "ciilock") return 1;
            return String(a.name || a.code).localeCompare(String(b.name || b.code));
        });
    }, [brands]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axiosInstance.get("/queue/queueSettings");

                const defaultEmail = data.daily_batch_limit_email ?? "";
                setLimits({
                    domain: data.daily_batch_limit_domain ?? "",
                    domainCutoffDays: data.domain_cutoff_days ?? "",
                    email: defaultEmail,
                    connect: data.daily_batch_limit_connect ?? "",
                    expandi_scraping: data.daily_batch_limit_expandi_scraping ?? "",
                    leverage_enhancement: data.daily_batch_limit_leverage_enhancement ?? "",
                });

                const incomingBrands = Array.isArray(data.brands) && data.brands.length ? data.brands : FALLBACK_BRANDS;
                setBrands(incomingBrands);

                const incomingByBrand = data.brand_email_limits || {};
                const merged = {};
                for (const b of incomingBrands) {
                    merged[b.code] = incomingByBrand[b.code] ?? defaultEmail;
                }
                setBrandEmailLimits(merged);
            } catch (_err) {
                toast.error("Failed to load queue settings");
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleGlobalChange = (e) => {
        const { name, value } = e.target;
        setLimits((prev) => ({ ...prev, [name]: value }));
    };

    const handleBrandEmailChange = (code, value) => {
        setBrandEmailLimits((prev) => ({ ...prev, [code]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axiosInstance.put("/queue/queueSettings", {
                daily_batch_limit_domain: limits.domain,
                domain_cutoff_days: limits.domainCutoffDays,
                daily_batch_limit_email: limits.email,
                daily_batch_limit_connect: limits.connect,
                daily_batch_limit_expandi_scraping: limits.expandi_scraping,
                daily_batch_limit_leverage_enhancement: limits.leverage_enhancement,
                brand_email_limits: brandEmailLimits,
            });
            toast.success("Settings updated");
        } catch (_err) {
            toast.error("Failed to update settings");
        } finally {
            setSaving(false);
        }
    };

    const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
    const labelClass = "block text-sm font-semibold text-slate-700";
    const inputClass =
        "mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/20";
    const iconBtnClass =
        "inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20";
    const primaryBtnClass =
        "inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/20 disabled:cursor-not-allowed disabled:bg-slate-400";

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className={`${cardClass} w-full max-w-md p-6`}>
                    <div className="flex items-center gap-3 text-slate-700">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm font-semibold">Loading settings...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="mx-auto w-full max-w-3xl">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Queue Settings</h1>
                        <p className="mt-1 text-sm text-slate-600">Configure daily batch limits for queue processing.</p>
                    </div>

                    <button onClick={() => window.history.back()} className={iconBtnClass} type="button">
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </button>
                </div>

                <div className={`${cardClass} p-6 sm:p-8`}>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-5">
                            <div>
                                <label htmlFor="domain" className={labelClass}>
                                    Domain Batch Limit
                                </label>
                                <input type="number" id="domain" name="domain" value={limits.domain} onChange={handleGlobalChange} min="0" required className={inputClass} />
                                <p className="mt-1 text-xs text-slate-500">Max domains processed per day.</p>
                            </div>

                            <div>
                                <label htmlFor="domainCutoffDays" className={labelClass}>
                                    Domain Cutoff Days
                                </label>
                                <input
                                    type="number"
                                    id="domainCutoffDays"
                                    name="domainCutoffDays"
                                    value={limits.domainCutoffDays}
                                    onChange={handleGlobalChange}
                                    min="0"
                                    required
                                    className={inputClass}
                                />
                                <p className="mt-1 text-xs text-slate-500">Domains older than this many days are treated as stale in domain workflows.</p>
                            </div>

                            <div>
                                <label htmlFor="email" className={labelClass}>
                                    Email Batch Limit (Default / CiiLOCK)
                                </label>
                                <input type="number" id="email" name="email" value={limits.email} onChange={handleGlobalChange} min="0" required className={inputClass} />
                                <p className="mt-1 text-xs text-slate-500">Default email batch cap. Used by CiiLOCK and as fallback for other brands.</p>
                            </div>

                            <div>
                                <label htmlFor="expandi_scraping" className={labelClass}>
                                    Linkedin Scraping Batch Limit
                                </label>
                                <input
                                    type="number"
                                    id="expandi_scraping"
                                    name="expandi_scraping"
                                    value={limits.expandi_scraping}
                                    onChange={handleGlobalChange}
                                    min="0"
                                    required
                                    className={inputClass}
                                />
                                <p className="mt-1 text-xs text-slate-500">Max lead lists scraped per day.</p>
                            </div>

                            <div>
                                <label htmlFor="leverage_enhancement" className={labelClass}>
                                    Leverage Enhancement Batch Limit
                                </label>
                                <input
                                    type="number"
                                    id="leverage_enhancement"
                                    name="leverage_enhancement"
                                    value={limits.leverage_enhancement}
                                    onChange={handleGlobalChange}
                                    min="0"
                                    required
                                    className={inputClass}
                                />
                                <p className="mt-1 text-xs text-slate-500">Max deep researches per day.</p>
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="mb-3">
                                <h2 className="text-sm font-bold text-slate-900">Email Batch Limit by Brand</h2>
                                <p className="mt-1 text-xs text-slate-600">Shown for all brands regardless of the current top-level brand switcher.</p>
                            </div>
                            <div className="space-y-3">
                                {orderedBrands.map((b) => (
                                    <div key={b.code} className="grid grid-cols-1 sm:grid-cols-[1fr_180px] items-center gap-2">
                                        <div className="text-sm font-semibold text-slate-800">{b.name || b.code}</div>
                                        <input
                                            type="number"
                                            min="0"
                                            value={brandEmailLimits[b.code] ?? ""}
                                            onChange={(e) => handleBrandEmailChange(b.code, e.target.value)}
                                            className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-2">
                            <button type="submit" disabled={saving} className={primaryBtnClass}>
                                {saving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Save
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="mt-4 text-xs text-slate-500">Changes apply after the server acknowledges the updated limits.</div>
            </div>
        </div>
    );
};

export default QueueSetting;
